/*
 * ReelCommentsSheet — Phase 1D (RGM Reels)
 * ========================================
 * Mobile-first bottom-sheet modeled on components/CommentsDrawer.jsx
 * but talking to the Reel comment endpoints:
 *   GET  /api/reels/{reel_id}/comments
 *   POST /api/reels/{reel_id}/comments
 *   DELETE /api/reels/{reel_id}/comments/{comment_id}
 *
 * Reuses the existing comment storage (db.comments) via the backend
 * — no parallel comment system. Keeps V1 simple: flat list, add
 * comment, delete your own / owner delete, visible identity + time.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const formatRelative = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = Math.max(0, (Date.now() - t) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const ReelCommentsSheet = ({ open, onClose, reel, currentUser, onCountChange }) => {
  const reelId = reel?.reel_id || null;
  const creatorId = reel?.creator_id || reel?.creator?.user_id || null;
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!reelId) return;
    setLoading(true);
    try {
      const r = await api.get(`/reels/${reelId}/comments`);
      const rows = r.data?.comments || [];
      setComments(rows);
      if (typeof onCountChange === "function") onCountChange(rows.length);
    } catch (_e) {
      // soft fail
    } finally {
      setLoading(false);
    }
  }, [reelId, onCountChange]);

  useEffect(() => { if (open) fetchComments(); }, [open, fetchComments]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!currentUser) { toast.error("Please sign in to comment"); return; }
    const body = text.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/reels/${reelId}/comments`, { content: body });
      const next = [...comments, r.data.comment];
      setComments(next);
      setText("");
      if (typeof onCountChange === "function") onCountChange(next.length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (comment_id) => {
    try {
      await api.delete(`/reels/${reelId}/comments/${comment_id}`);
      const next = comments.filter((c) => c.comment_id !== comment_id);
      setComments(next);
      if (typeof onCountChange === "function") onCountChange(next.length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete comment");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-center"
      data-testid="reel-comments-sheet"
      role="dialog"
      aria-label="Reel comments"
    >
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
        data-testid="reel-comments-backdrop"
      />
      <div
        className="relative w-full sm:w-[520px] max-h-[85vh] sm:max-h-[80vh] bg-[#0f0c18] border-t sm:border border-purple-500/30 sm:rounded-xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-purple-300">Comments</p>
            <p className="text-sm text-white font-semibold truncate">
              {reel?.caption?.slice(0, 48) || "This Reel"} · {comments.length}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none px-2"
            aria-label="Close comments"
            data-testid="reel-comments-close"
          >×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <p className="text-gray-500 text-sm text-center py-6">Loading comments…</p>
          ) : comments.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">Be the first to comment.</p>
          ) : (
            comments.map((c) => {
              const canDelete = !!currentUser && (
                c.user_id === currentUser.user_id ||
                creatorId === currentUser.user_id ||
                currentUser.role === "admin"
              );
              return (
                <div
                  key={c.comment_id}
                  className="flex gap-2"
                  data-testid={`reel-comment-${c.comment_id}`}
                >
                  <img
                    src={getProfilePhotoUrl({
                      profile_photo: c.user_profile_photo,
                      picture: c.user_picture,
                    })}
                    alt={c.user_name || "artist"}
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0 bg-gray-900/60 rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <Link
                        to={`/artist/${c.user_id}`}
                        onClick={onClose}
                        className="text-xs text-purple-300 font-semibold truncate hover:underline"
                      >{c.user_name || "RGM Artist"}</Link>
                      <span className="text-[10px] text-gray-500 shrink-0">{formatRelative(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-white whitespace-pre-wrap break-words">{c.content}</p>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(c.comment_id)}
                        className="mt-1 text-[11px] text-red-400 hover:text-red-300"
                        data-testid={`reel-comment-delete-${c.comment_id}`}
                      >Delete</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {currentUser ? (
          <div className="border-t border-gray-800 p-3 flex items-end gap-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a comment…"
              rows={2}
              maxLength={1000}
              className="flex-1 bg-black/50 border border-gray-700 rounded px-3 py-2 text-white text-sm min-h-[56px] resize-none"
              data-testid="reel-comment-input"
            />
            <Button
              onClick={handleSubmit}
              disabled={submitting || !text.trim()}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shrink-0"
              data-testid="reel-comment-submit"
            >{submitting ? "Posting…" : "Post"}</Button>
          </div>
        ) : (
          <div className="border-t border-gray-800 p-3 text-center text-sm text-gray-400">
            Sign in to join the conversation.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReelCommentsSheet;
